/** @jsxImportSource @emotion/react */

import React, { useMemo, useRef } from 'react'
import * as classes from './styles'
import { useDraggableChart } from './hooks'

export const columnChartClasses = {
  oy: 'column-chart-oy',
  oyValue: 'column-chart-oy-value',
  chartContainer: 'column-chart-container',
  column: 'column-chart-column',
  oxValue: 'column-chart-oy-value',
}

type Value = {
  xAxisValue: number
  yAxisValue: number
}

type ScaleOptions = {
  ticks?: {
    count?: number
    callback?: (tickValue: number | string) => string | React.ReactNode
  }
  suggestedMax?: number
}

type Options = {
  scales: {
    y?: ScaleOptions
    x?: ScaleOptions
  }
}

type Props<T> = {
  values: T[]
  options?: Partial<Options>
  className?: string
  renderColumn?: (value: T, percentage: number, index: number) => React.ReactNode
}

const DEFAULT_Y_TICKS_COUNT = 5

export function ColumnChart<T extends Value>({ values, options, className, renderColumn }: Props<T>) {
  const chartRef = useRef<HTMLDivElement | null>(null)

  useDraggableChart(chartRef)

  const maxYValue = useMemo(() => {
    const max = Math.max(...values.map(({ yAxisValue }) => yAxisValue))
    const suggestedMax = options?.scales?.y?.suggestedMax || 0

    if (!max && suggestedMax) {
      return suggestedMax
    }

    return max
  }, [values, options?.scales?.y?.suggestedMax])

  const oy = useMemo(() => {
    const ticksCount =
      options?.scales?.y?.ticks?.count ||
      (maxYValue < DEFAULT_Y_TICKS_COUNT ? Math.ceil(maxYValue) : DEFAULT_Y_TICKS_COUNT)

    const step = maxYValue / ticksCount

    const tickCallback = options?.scales?.y?.ticks?.callback || ((tickValue) => tickValue)

    return Array.from({ length: ticksCount + 1 })
      .map((_, index) => tickCallback(step * index))
      .reverse()
  }, [options?.scales, maxYValue])

  return (
    <div css={classes.wrapper} className={className}>
      <div css={classes.oy} className={columnChartClasses.oy}>
        {oy.map((value, i) => (
          <div key={i} css={classes.oyValue(i === oy.length - 1)} className={columnChartClasses.oyValue}>
            {value}
          </div>
        ))}
      </div>
      <div ref={chartRef} css={classes.chart} className={columnChartClasses.chartContainer}>
        {values.map((value, index) => {
          const { xAxisValue, yAxisValue } = value
          const percentage = maxYValue ? Math.round((yAxisValue / maxYValue) * 100) : 0

          return (
            <React.Fragment key={index}>
              {renderColumn ? (
                renderColumn(value, percentage, index)
              ) : (
                <div css={classes.column} className={columnChartClasses.column}>
                  <div css={[classes.item(percentage), classes.growAnimation(index)]} />
                </div>
              )}
              <div css={classes.value} className={columnChartClasses.oxValue}>
                {options?.scales?.x?.ticks?.callback?.(xAxisValue) || xAxisValue}
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
